/**
 * Conditional Fields Utility
 *
 * Handles conditional field visibility based on dependencies
 */

/**
 * Check if a field should be visible based on its dependencies
 * @param {Object} field - The field configuration
 * @param {Object} formData - Current form data
 * @returns {boolean} - Whether the field should be shown
 */
export const shouldShowField = (field, formData) => {
  // If no dependencies, always show
  if (!field.dependsOn) {
    return true;
  }

  const { fieldKey, value, includes, equals, notEquals, isNotEmpty } = field.dependsOn;

  // Get the value of the field this depends on
  const dependentValue = formData[fieldKey];

  // Check if dependent field has a value (for isNotEmpty condition)
  if (isNotEmpty !== undefined) {
    const hasValue = dependentValue &&
                     dependentValue !== '' &&
                     dependentValue !== null &&
                     dependentValue !== undefined &&
                     (!Array.isArray(dependentValue) || dependentValue.length > 0);
    return isNotEmpty ? hasValue : !hasValue;
  }

  // Check equals condition
  if (equals !== undefined) {
    return dependentValue === equals;
  }

  // Check notEquals condition
  if (notEquals !== undefined) {
    return dependentValue !== notEquals;
  }

  // Check includes condition (for arrays/multi-select)
  if (includes !== undefined) {
    if (Array.isArray(dependentValue)) {
      return dependentValue.includes(includes);
    }
    // Handle string values too
    if (typeof dependentValue === 'string') {
      return dependentValue === includes;
    }
    return false;
  }

  // Check value condition (legacy support)
  if (value !== undefined) {
    if (Array.isArray(dependentValue)) {
      return dependentValue.includes(value);
    }
    return dependentValue === value;
  }

  return true;
};

/**
 * Filter fields based on conditional visibility
 * @param {Array} fields - Array of field configurations
 * @param {Object} formData - Current form data
 * @returns {Array} - Filtered fields that should be visible
 */
export const filterConditionalFields = (fields, formData) => {
  return fields.filter(field => shouldShowField(field, formData));
};

/**
 * Get field key from field name (convert to snake_case)
 * @param {string} fieldName - The field name
 * @returns {string} - The field key
 */
export const getFieldKey = (fieldName) => {
  return fieldName.toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '');
};
